import React, {
  FunctionComponent,
  useEffect,
  useContext,
  useState,
} from "react";
import { RouteComponentProps, withRouter } from "react-router";
import URI from "urijs";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  ActionType,
  SlsxRedirectQueryParams,
  MctToastType,
  UserLanguage,
} from "../../@types";
import {
  fetchCurrentPlan,
  logError,
  useTranslate,
  isOutsideOpenEnrollment,
} from "../../helpers";
import { getBeneInfo, getBeneDrugs, getBeneOecStatus } from "../../api";
import { AppContext } from "../../app/store";
import routes from "../../app/routes";
import { AppPage, LoadingMask } from "../../components";
import { fetchPlanIdForNextYear } from "../../helpers/planHelpers";
import { getToastRoute } from "../../helpers";
import { parseDegradedIntegrations } from "../../helpers/loginHelpers";

const LoginCallbackHandler: FunctionComponent<RouteComponentProps> = props => {
  const t = useTranslate();
  const {
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  } = useFlags();
  const [beneFetched, setBeneFetched] = useState(false);

  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });

  // The SLS code, which is transmited to us via query param (aka the 'search' portion of the location object)
  const { search } = props.location;

  const { req_token, relay } = URI.parseQuery(
    search
  ) as SlsxRedirectQueryParams;
  const code = req_token;

  const { state, dispatch } = useContext(AppContext);

  const getBeneficiary = async (): Promise<void> => {
    let userLang = UserLanguage.ENGLISH;

    if (relay) {
      try {
        // relay should be a base64-encoded string
        const { lang }: { lang: UserLanguage } = JSON.parse(atob(relay));

        userLang = lang;
      } catch (e) {
        logError("Failed to parse the relay (LoginCallbackHandler)", e);
      }
    }

    if (code) {
      // Clear out any previous bene info, if not already cleared via sessionStorage
      dispatch({ type: ActionType.RESET_STATE });

      try {
        const { beneficiary: beneInfo, headers } = await getBeneInfo(code);

        const degradedIntegrations = parseDegradedIntegrations(headers);

        if (degradedIntegrations.length) {
          dispatch({
            type: ActionType.UPDATE_DEGRADED_INTEGRATIONS,
            payload: degradedIntegrations,
          });
        }

        dispatch({
          type: ActionType.ADD_BENEFICIARY,
          payload: beneInfo,
        });
        setBeneFetched(true);

        dispatch({
          type: ActionType.UPDATE_LIS,
          payload: beneInfo?.lis_level,
        });
        dispatch({
          type: ActionType.UPDATE_FUTURE_LIS,
          payload: beneInfo?.future_lis_level,
        });

        try {
          const drugs = await getBeneDrugs();

          dispatch({ type: ActionType.SET_PRESCRIPTIONS, payload: drugs });
        } catch (e) {
          logError("Failed to get beneficiary drugs (LoginCallbackHandler)", e);
        }

        if (beneInfo?.current_coverage.length) {
          let nextYearPlanId;
          if (!outsideOpenEnrollment || mctYpOeNextYear) {
            nextYearPlanId = await fetchPlanIdForNextYear(
              beneInfo.current_coverage[0],
              "LoginCallbackHandler"
            );
          }
          await fetchCurrentPlan(
            beneInfo,
            state.pharmacies,
            beneInfo.lis_level,
            dispatch,
            nextYearPlanId,
            beneInfo.future_lis_level
          );
        }

        props.history.replace(`${routes.beneLanding}?lang=${userLang}`);
      } catch (e) {
        logError("Failed to retrieve beneficiary (LoginCallbackHandler)", e);
        props.history.replace(
          getToastRoute(
            `${routes.questionRouting}?lang=${userLang}`,
            MctToastType.BENE_FAILED_LOGIN
          )
        );
      }
    } else if (state.beneficiary) {
      props.history.replace(`${routes.beneLanding}?lang=${userLang}`);
    } else {
      props.history.replace(
        getToastRoute(
          `${routes.questionRouting}?lang=${userLang}`,
          MctToastType.BENE_FAILED_LOGIN
        )
      );
    }
  };

  useEffect(() => {
    getBeneficiary();
  }, []);

  const fetchOECStatus = async (): Promise<void> => {
    try {
      if (state.year) {
        const status = await getBeneOecStatus(state.year);

        dispatch({
          type: ActionType.UPDATE_BENEFICIARY_OEC,
          payload: status,
        });
      }
    } catch (e) {
      logError(
        "Failed to get beneficiary OEC status (LoginCallbackHandler)",
        e
      );
    }
  };

  useEffect(() => {
    if (state.year && beneFetched) {
      fetchOECStatus();
    }
  }, [state.year, beneFetched]);

  return (
    <AppPage>
      <LoadingMask
        loading={true}
        message={t("loading.retrieving_your_information")}
      />
    </AppPage>
  );
};

export default withRouter(LoginCallbackHandler);
